import { quoteService } from '@/modules/quotes/services'

import { followUpService } from './FollowUpService'
import { opportunityService } from './OpportunityService'
import { pipelineService } from './PipelineService'
import { reminderService } from './ReminderService'
import { statisticsService } from './StatisticsService'
import { resetCrmMemory } from './storage'
import { whatsappService } from './WhatsappService'
import type {
  CrmPermissions,
  FollowUpResult,
  LostReasonCode,
  OpportunityFilters,
  OpportunityStage,
} from './types'
import { DEFAULT_CRM_PERMISSIONS } from './types'

/**
 * CrmEngine — facade for Painel de Oportunidades.
 * Prepared for WhatsApp API, Email, IA, Google Calendar and Outlook.
 */
export class CrmEngine {
  private permissions: CrmPermissions = { ...DEFAULT_CRM_PERMISSIONS }

  setPermissions(patch: Partial<CrmPermissions>): CrmPermissions {
    this.permissions = { ...this.permissions, ...patch }
    return this.permissions
  }

  getPermissions(): CrmPermissions {
    return { ...this.permissions }
  }

  assertPermission(action: keyof CrmPermissions): void {
    if (!this.permissions[action]) throw new Error(`Permissão negada: ${action}`)
  }

  syncFromQuotes() {
    this.assertPermission('view')
    const quotes = quoteService.list()
    const opportunities = opportunityService.syncFromQuotes(quotes)
    reminderService.syncFromOpportunities(opportunities)
    return opportunities
  }

  list(filters: OpportunityFilters = {}) {
    this.assertPermission('view')
    return opportunityService.search(filters)
  }

  getById(id: string) {
    this.assertPermission('view')
    return opportunityService.getById(id)
  }

  pipeline(filters: OpportunityFilters = {}) {
    this.assertPermission('view')
    return pipelineService.build(filters)
  }

  moveStage(id: string, stage: OpportunityStage) {
    this.assertPermission('edit')
    const next = pipelineService.move(id, stage)
    reminderService.syncFromOpportunities()
    return next
  }

  statistics(filters: OpportunityFilters = {}) {
    this.assertPermission('view')
    return statisticsService.compute(filters)
  }

  reminders() {
    this.assertPermission('view')
    return reminderService.syncFromOpportunities()
  }

  listReminders() {
    this.assertPermission('view')
    return reminderService.listPending()
  }

  dismissReminder(id: string) {
    this.assertPermission('edit')
    return reminderService.dismiss(id)
  }

  contact(opportunityId: string) {
    this.assertPermission('edit')
    const opportunity = opportunityService.getById(opportunityId)
    if (!opportunity) throw new Error('Oportunidade não encontrada')
    const draft = whatsappService.buildFollowUpMessage(opportunity)
    const opened = whatsappService.openDraft(draft)
    opportunityService.recordContact(opportunityId)
    followUpService.create({
      opportunityId,
      contactType: 'whatsapp',
      notes: 'Mensagem de follow-up gerada',
    })
    reminderService.syncFromOpportunities()
    return { draft, opened, opportunity: opportunityService.getById(opportunityId)! }
  }

  registerResponse(
    opportunityId: string,
    result: FollowUpResult,
    options?: { notes?: string; lostReasonCode?: LostReasonCode; lostReasonNotes?: string },
  ) {
    this.assertPermission('edit')
    const response = followUpService.registerResponse(opportunityId, result, options)
    reminderService.syncFromOpportunities()
    return response
  }

  timeline(opportunityId: string) {
    this.assertPermission('view')
    return opportunityService.listTimeline(opportunityId)
  }

  followUps(opportunityId: string) {
    this.assertPermission('view')
    return followUpService.list(opportunityId)
  }

  lostReasons() {
    return opportunityService.listLostReasons()
  }

  remove(id: string) {
    this.assertPermission('delete')
    opportunityService.remove(id)
  }

  exportCsv(filters: OpportunityFilters = {}): string {
    this.assertPermission('view')
    const rows = this.list(filters)
    const header = [
      'id',
      'quoteCode',
      'customerName',
      'customer',
      'stage',
      'expectedValue',
      'daysIdle',
      'responsibleUser',
      'city',
      'phone',
    ]
    const lines = [
      header.join(';'),
      ...rows.map((o) =>
        [
          o.id,
          o.quoteCode,
          o.customerName ?? '',
          o.status,
          o.stage,
          o.expectedValue,
          o.daysIdle,
          o.responsibleUser,
          o.customerCity ?? '',
          o.customerPhone ?? '',
        ]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(';'),
      ),
    ]
    return lines.join('\n')
  }

  exportJson(filters: OpportunityFilters = {}) {
    this.assertPermission('view')
    return {
      engine: 'crm-opportunities',
      exportedAt: new Date().toISOString(),
      items: this.list(filters),
      statistics: this.statistics(filters),
    }
  }

  exportPdfMeta(filters: OpportunityFilters = {}) {
    this.assertPermission('view')
    return {
      ok: true as const,
      message: 'PDF do CRM preparado para motor futuro',
      count: this.list(filters).length,
    }
  }

  /** Future channel stubs */
  prepareWhatsappApi() {
    return { channel: 'whatsapp-api', status: 'architecture-ready' }
  }

  prepareEmail() {
    return { channel: 'email', status: 'architecture-ready' }
  }

  prepareAi() {
    return { channel: 'ai', status: 'architecture-ready' }
  }

  prepareGoogleCalendar() {
    return { channel: 'google-calendar', status: 'architecture-ready' }
  }

  prepareOutlook() {
    return { channel: 'microsoft-outlook', status: 'architecture-ready' }
  }

  resetMemory(): void {
    resetCrmMemory()
  }
}

export const crmEngine = new CrmEngine()
